import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MathBlock } from "../components/MathRender";
import { StatusBadge, VerdictLabel } from "../components/StatusBadge";
import { ArrowLeft, Scale, Paperclip, Download, Upload, Gavel, Clock } from "lucide-react";

const LABELS = [
  { v: "computational_check", t: "Computational Check" },
  { v: "expert_review", t: "Expert Review" },
  { v: "formally_checked_proof", t: "Formally Checked Proof" },
];

function VerdictPanel({ submission, onDone }) {
  const [verdict, setVerdict] = useState("accept");
  const [label, setLabel] = useState("expert_review");
  const [comments, setComments] = useState("");
  const [confidence, setConfidence] = useState(4);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/verify/submissions/${submission.id}/review`, { verdict, label, comments, confidence: Number(confidence) });
      toast.success(`Review recorded — submission is now ${data.status}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit review");
    } finally { setLoading(false); }
  };

  return (
    <div className="axiom-card p-6" data-testid="verdict-panel">
      <h3 className="font-display text-lg text-slate-900">Record your independent verdict</h3>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <button data-testid="verdict-accept" onClick={() => setVerdict("accept")}
          className={`rounded-lg border px-4 py-3 text-sm ${verdict === "accept" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-[#CBD5E1] text-slate-600"}`}>Accept</button>
        <button data-testid="verdict-reject" onClick={() => setVerdict("reject")}
          className={`rounded-lg border px-4 py-3 text-sm ${verdict === "reject" ? "border-rose-500 bg-rose-50 text-rose-700" : "border-[#CBD5E1] text-slate-600"}`}>Reject</button>
      </div>
      <label className="eyebrow mt-4 block">Verification label</label>
      <select data-testid="verdict-label-select" value={label} onChange={(e) => setLabel(e.target.value)}
        className="mt-2 w-full rounded-lg border border-[#CBD5E1] bg-slate-50 px-3 py-2.5 text-slate-900 outline-none focus:border-[#E61A27]">
        {LABELS.map((l) => <option key={l.v} value={l.v} className="bg-[#FFFFFF]">{l.t}</option>)}
      </select>
      <label className="eyebrow mt-4 block">Confidence (1–5): {confidence}</label>
      <input data-testid="verdict-confidence" type="range" min={1} max={5} value={confidence} onChange={(e) => setConfidence(e.target.value)} className="mt-2 w-full accent-[#E61A27]" />
      <label className="eyebrow mt-4 block">Comments</label>
      <textarea data-testid="verdict-comments" rows={3} value={comments} onChange={(e) => setComments(e.target.value)}
        className="mt-2 w-full rounded-lg border border-[#CBD5E1] bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none focus:border-[#E61A27]" />
      <button data-testid="verdict-submit" onClick={submit} disabled={loading} className="btn-crimson mt-4 w-full">
        {loading ? "Recording…" : "Submit review"}
      </button>
    </div>
  );
}

export default function SubmissionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [appealReason, setAppealReason] = useState("");

  const load = useCallback(() => {
    api.get(`/verify/submissions/${id}`).then((r) => setS(r.data)).catch(() => toast.error("Not authorized"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/solve/submissions/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File attached (private, signed access)");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const downloadFile = async (f) => {
    try {
      const res = await api.get(`/files/${f.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download file");
    }
  };

  const fileAppeal = async () => {
    if (!appealReason.trim()) { toast.error("A reason is required to appeal"); return; }
    try {
      await api.post(`/verify/submissions/${id}/appeal`, { reason: appealReason });
      toast.success("Appeal filed — escalated to the Tribunal");
      setAppealReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not file appeal");
    }
  };

  if (!s) return <div className="px-6 py-20 text-center text-slate-500">Loading…</div>;

  const alreadyReviewed = s.reviews?.some((r) => r.reviewer_id === user?.id);
  const isReviewer = user?.role === "reviewer";
  const canReview = isReviewer && s.contributor_id !== user.id && !alreadyReviewed && !["accepted", "rejected", "disputed"].includes(s.status);
  const isOwner = user?.id === s.contributor_id;
  const isCustomer = user && (user.role === "customer" || user.role === "admin");

  return (
    <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8" data-testid="submission-detail-page">
      <Link to="/verify" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl text-slate-900 sm:text-3xl">{s.problem_title}</h1>
        <StatusBadge status={s.status} />
      </div>
      <p className="mt-1 text-sm text-slate-500">Submission v{s.version} · by {s.contributor_name}</p>

      <div className="mt-6"><MathBlock>{s.content_latex}</MathBlock></div>
      {s.notes && <p className="mt-3 rounded-lg border border-[#CBD5E1] bg-slate-100 p-3 text-sm text-slate-600"><span className="text-[#D4AF37]">Notes:</span> {s.notes}</p>}

      <div className="mt-6" data-testid="attachments-section">
        <p className="eyebrow mb-3 flex items-center gap-2"><Paperclip className="h-4 w-4" /> Private attachments</p>
        <div className="space-y-2">
          {(s.attachments || []).map((f) => (
            <button key={f.id} data-testid={`attachment-${f.id}`} onClick={() => downloadFile(f)} className="axiom-card flex w-full items-center justify-between p-3 text-left">
              <span className="flex items-center gap-2 text-sm text-slate-700"><Download className="h-4 w-4 text-[#E61A27]" /> {f.original_filename}</span>
              <span className="text-xs text-slate-400">{Math.max(1, Math.round((f.size || 0) / 1024))} KB</span>
            </button>
          ))}
          {(!s.attachments || s.attachments.length === 0) && <p className="text-sm text-slate-500">No files attached. Submissions stay private with signed access.</p>}
        </div>
        {isOwner && (
          <label className="btn-ghost mt-3 inline-flex cursor-pointer !py-2 text-sm" data-testid="attachment-upload-label">
            <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Attach PDF / proof file"}
            <input data-testid="attachment-upload-input" type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.txt,.csv" onChange={uploadFile} disabled={uploading} />
          </label>
        )}
      </div>

      <div className="mt-8">
        <p className="eyebrow mb-3">Review ledger ({s.reviews?.length || 0}/2)</p>
        <div className="space-y-3">
          {s.reviews?.map((r) => (
            <div key={r.id} className="axiom-card p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-900">{r.reviewer_name}</span>
                <span className={`text-sm font-mono ${r.verdict === "accept" ? "text-emerald-700" : "text-rose-700"}`}>{r.verdict.toUpperCase()}</span>
              </div>
              <div className="mt-2 flex items-center gap-2"><VerdictLabel label={r.label} /><span className="text-xs text-slate-500">confidence {r.confidence}/5 · honorarium ${r.honorarium}</span></div>
              {r.comments && <p className="mt-2 text-sm text-slate-500">{r.comments}</p>}
            </div>
          ))}
          {(!s.reviews || s.reviews.length === 0) && <p className="text-sm text-slate-500">No reviews yet.</p>}
        </div>
      </div>

      {s.status === "disputed" && (
        <div className="axiom-card mt-6 border-rose-200 bg-rose-50 p-5" data-testid="dispute-notice">
          <p className="flex items-center gap-2 text-sm text-rose-700"><Scale className="h-4 w-4" /> Reviews conflicted — escalated to the Tribunal for conflict-free adjudication.</p>
        </div>
      )}

      {s.status === "accepted" && s.appeal_window_open && isCustomer && (
        <div className="axiom-card mt-6 border-amber-200 bg-amber-50 p-5" data-testid="appeal-panel">
          <p className="flex items-center gap-2 text-sm text-amber-800">
            <Clock className="h-4 w-4" /> Appeal window open — about {Math.max(1, Math.ceil((s.appeal_seconds_left || 0) / 86400))} day(s) left. Your silence will not block acceptance; after the window closes it is final.
          </p>
          <textarea data-testid="appeal-reason" rows={2} value={appealReason} onChange={(e) => setAppealReason(e.target.value)} placeholder="Reason for appeal (goes to the Tribunal)"
            className="mt-3 w-full rounded-lg border border-[#CBD5E1] bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none focus:border-[#E61A27]" />
          <button data-testid="appeal-submit" onClick={fileAppeal} className="btn-crimson mt-3 !py-2 text-sm"><Gavel className="h-4 w-4" /> File an appeal</button>
        </div>
      )}
      {s.status === "accepted" && s.finalized && (
        <p className="mt-6 text-sm text-emerald-700" data-testid="appeal-finalized">Acceptance is final — the seven-day appeal window has closed.</p>
      )}
      {s.status === "accepted" && s.appeal_window_open && !isCustomer && (
        <p className="mt-6 text-sm text-slate-500" data-testid="appeal-open-note">Accepted. A seven-day customer appeal window is open before payout is final.</p>
      )}

      {canReview && <div className="mt-6"><VerdictPanel submission={s} onDone={load} /></div>}
      {isReviewer && alreadyReviewed && s.status === "under_review" && (
        <p className="mt-6 text-sm text-slate-500" data-testid="awaiting-second-review">Your review is recorded. Awaiting a second independent reviewer.</p>
      )}
    </div>
  );
}
